import FilterTabs from '@common/components/filter-tabs';
import Stack from '@common/components/stack';
import { EPerformanceActivityFilterTypes } from '@modules/performance/definitions';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEvaluationStore } from './store';
import { Button } from '@common/components/button';
import { useSelector } from 'react-redux';
import { StoreState } from '@common/types/store';
import { APIResponseNotesList, NotesFormData } from '@modules/core/types';
import { useRequest } from '@common/services/api';
import {
  createNote,
  deleteNote,
  getNotesEndpoint,
} from '@modules/core/containers/profile/notes/api/notes-api';
import { selected } from '@modules/organisation/selectors/organisation';
import { useAppSelector, useCanAccess } from '@common/hooks';
import { Note } from '@modules/core/components/note';
import { EPermissions } from '@common/definitions';
import AddNoteForm from '@modules/core/forms/add-note';
import { NoNotes } from '@modules/core/containers/profile/notes/no-notes';
import Spinner from '@common/components/spinner';
import RecognitionCard from '@common/components/recognition-card';
import { RecognitionObject } from '@modules/admin/containers/highfive/types';
import { AlertService } from '@common/services/alert';
import Placeholder from '@common/components/placeholder';

const Activity = ({ userId }: { userId: string }) => {
  const { t } = useTranslation();
  const { selectedFilter, handleFilterChange } = useEvaluationStore();
  const [isNoteAddModalVisible, setIsNoteAddModalVisible] = useState(false);
  const loggedUser = useSelector((state: StoreState) => state.loggedUser.user);
  const organisation = useAppSelector(selected);

  const canCreateNotes = useCanAccess({
    permissions: EPermissions.ORGANISATION_USERS_UPDATE,
    networkPermissions: EPermissions.NETWORK_USERS_UPDATE,
    networks: loggedUser?.scopes?.networks || [],
  });

  const apiConfig = {
    transform: (res: APIResponseNotesList) => {
      const notesWithUsers = res.data.map((note) => {
        return {
          ...note,
          created_by:
            res.meta.related.users.find(
              (creator) => creator.id === note.created_by
            ) || note.created_by,
          deleted_by:
            res.meta.related.users.find(
              (creator) => creator.id === note.deleted_by
            ) || note.deleted_by,
        };
      });

      return {
        ...res,
        data: notesWithUsers,
      };
    },
  };

  const {
    data: notes,
    setData: setNotes,
    loading: notesLoading,
    refetch: refetchNotes,
  } = useRequest<APIResponseNotesList>(
    getNotesEndpoint(organisation.id, userId),
    apiConfig
  );

  const highfiveApiConfig = {
    transform: (res: any) => {
      return {
        ...res,
        data: res.data.map((recognition: RecognitionObject) => ({
          ...recognition,
          sender_id: recognition.sender_id
            ? res.meta.related.users.find(
                (user: any) => user.id === recognition.sender_id
              )
            : null,
          recipient_id: recognition.recipient_id
            ? res.meta.related.users.find(
                (user: any) => user.id === recognition.recipient_id
              )
            : null,
        })),
      };
    },
  };

  const {
    data: highfivesResponse,
    loading: highfivesLoading,
  } = useRequest(
    `/v1/organisations/${organisation.id}/recognitions?recipient_id=${userId}`,
    highfiveApiConfig
  );

  const handleAddNote = async (formData: NotesFormData) => {
    await createNote(organisation.id, userId, formData);
    setIsNoteAddModalVisible(false);
    refetchNotes();
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      const res = await deleteNote(organisation.id, userId, noteId);
      if (res.data.hard_delete) {
        setNotes(notes.filter((note) => note.id !== noteId));
      } else {
        setNotes(notes.map((note) => (note.id === noteId ? {
 ...note, text: '', attachments: [], deleted_by: loggedUser, deleted_at: new Date().toString()
} : note)));
      }
      AlertService.success(t('core:note_deleted_successfully'));
    } catch (error: any) {
      AlertService.forStatus(error.status, {
        warning: t('core:note_deleted_warning'),
        error: t('core:note_deleted_error'),
      });
    }
  };

  const filters = [
    {
      id: EPerformanceActivityFilterTypes.NOTES,
      label: t('performance:activity_filter_notes'),
    },
    {
      id: EPerformanceActivityFilterTypes.HIGHFIVE,
      label: t('performance:activity_filter_highfive'),
    },
  ];
  return (
    <>
      <Stack
        direction="column"
        gap={6}
        className="tw-p-6 tw-h-[calc(100vh-300px)] tw-max-h-[calc(100vh-300px)] tw-overflow-y-auto"
      >
        <Stack direction="row" className="tw-justify-between">
          <FilterTabs
            options={filters}
            selected={selectedFilter}
            onChange={handleFilterChange}
          />
          {selectedFilter === EPerformanceActivityFilterTypes.NOTES && (
            <Button
              type="inverted-primary"
              icon="add"
              size="default"
              onClick={() => setIsNoteAddModalVisible(true)}
            >
              {t('performance:add_note')}
            </Button>
          )}
        </Stack>
        {selectedFilter === EPerformanceActivityFilterTypes.NOTES ? (
          notesLoading ? (
            <Spinner centered />
          ) : (
            // <Card containerClassName="AdminNotes__Card !tw-pt-0 !tw-pb-0">
            <>
              {notes && notes.length > 0 ? (
                notes.map((note) => (
                  <Note
                    key={note.id}
                    note={note}
                    showVisibility={canCreateNotes}
                    onDelete={() => handleDeleteNote(note.id)}
                  />
                ))
              ) : (
                <NoNotes
                  canCreateNotes={canCreateNotes}
                  setIsNoteAddModalVisible={setIsNoteAddModalVisible}
                />
              )}
            </>
            // </Card>
          )
        ) : selectedFilter === EPerformanceActivityFilterTypes.HIGHFIVE ? (
          highfivesLoading ? (
            <Spinner centered />
          ) : (
            <>
              {/* // <Card containerClassName="AdminNotes__Card !tw-pt-0 !tw-pb-0"> */}
              {highfivesResponse && highfivesResponse && highfivesResponse.length > 0 ? (
                highfivesResponse.map((highfive: RecognitionObject) => {
                  const recognition = {
                    receiver: highfive.recipient_id,
                    text: highfive.text,
                    values: highfive.values.map((value) => ({
                      id: String(value.id),
                      name: value.name,
                      description: value.description,
                    })),
                    shared: highfive.shared,
                  };

                  return (
                    <RecognitionCard
                      key={highfive.id}
                      recognition={recognition}
                      loggedUser={loggedUser}
                      user={highfive.sender_id}
                      match={{}}
                      enablePopovers={false}
                      isNotifiedUser
                    />
                  );
                })
              ) : (
                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-py-8">
                  <Placeholder title={t('performance:no_highfives_found')} />
                </div>
              )}
              {/* </Card> */}
            </>
          )
        ) : null}
      </Stack>
      {isNoteAddModalVisible && (
        <AddNoteForm
          isVisible={isNoteAddModalVisible}
          onSave={handleAddNote}
          handleClose={() => setIsNoteAddModalVisible(false)}
        />
      )}
    </>
  );
};

export default Activity;
